<script setup lang="ts">
import type { PaAPI } from '@rialtic/types'

const { $auth0, $datadog } = useNuxtApp()
const { getAllFlags, flags } = useAppConfiguration()

getAllFlags()

const { isAuthenticated, isLoading, user, loginWithRedirect, logout } = $auth0()

const signInLoading = ref(false)
const onSignIn = () => {
  signInLoading.value = true
  loginWithRedirect()
}

const onSignOut = async () => {
  $datadog.clearUser()
  await logout({
    logoutParams: { returnTo: `${window.location.origin}/` },
  })
}

const workspace = inject<ComputedRef<PaAPI.Get['/workspace/{id}']>>('workspace')
const workspaceUnderMaintenance = inject<ComputedRef<boolean>>(
  'workspace-under-maintenance',
)

const underMaintenance = computed(
  (): boolean =>
    Boolean(flags.value.maintenance) ||
    Boolean(workspaceUnderMaintenance?.value),
)

const isRialticUser = computed(() =>
  Boolean(user.value?.email?.endsWith('@rialtic.io')),
)
</script>

<template>
  <main
    v-if="!isAuthenticated"
    class="bg-surface flex min-h-screen w-full flex-col items-center justify-center space-y-10"
  >
    <div class="">
      <svg
        class="transition"
        :class="{
          'animate-pulse': isLoading,
        }"
        style="width: 200px"
        fill="none"
        viewBox="0 0 55 55"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M21.216 25.474a2.064 2.064 0 1 1 0 4.128 2.064 2.064 0 0 1 0-4.128ZM2.383 11.102a1.662 1.662 0 1 0 0-3.324 1.662 1.662 0 0 0 0 3.324Zm37.904 7.614a1.662 1.662 0 1 0 1.662-2.878l-7.317-4.225c-.775-.448-1.433-.459-2.204-.018l-3.44 1.998.009-3.98c-.004-.888-.342-1.452-1.117-1.9L16.09.907c-.774-.448-1.433-.458-2.204-.018L8.252 4.13c-.413.204-.715.472-.907.804-.192.332-.273.728-.243 1.188l.01 13.66c.004.889.343 1.453 1.117 1.9l5.922 3.42c.61.351 1.727.333 2.27-.608a1.7 1.7 0 0 0 .218-.76l.003-11.512 2.934 1.694.027 6.216c-.034.57.25 1.103.802 1.423.775.447 1.736.19 2.183-.586.16-.277.232-.53.25-.815l.006-6.786c-.004-.889-.342-1.453-1.117-1.9l-5.922-3.42c-.775-.447-1.767-.135-2.215.64a1.7 1.7 0 0 0-.217.76l-.004 11.512-2.933-1.694-.024-12.486 4.57-2.676 10.792 6.232.028 6.216c.021.603.304 1.135.803 1.423.331.192 1.036.377 1.687.015l5.396-3.084 6.598 3.81Zm4.58.726a1.662 1.662 0 1 0 2.878 1.662 1.662 1.662 0 0 0-2.878-1.662ZM19.32 48.467a1.662 1.662 0 0 0 1.662 2.878l7.317-4.225c.775-.447 1.113-1.012 1.117-1.9l-.01-3.98 3.441 1.999c.771.44 1.43.43 2.204-.018l11.788-6.807c.775-.448 1.114-1.012 1.118-1.9l.01-6.501c.03-.46-.051-.856-.242-1.188-.192-.332-.495-.601-.908-.805l-11.833-6.821c-.771-.44-1.429-.43-2.204.018l-5.922 3.419c-.608.352-1.151 1.33-.608 2.27.128.222.311.411.55.569l9.965 5.759-2.933 1.694-5.396-3.084c-.477-.315-1.08-.336-1.633-.017a1.582 1.582 0 0 0-.585 2.183c.16.277.343.467.582.624l5.873 3.399c.77.44 1.428.43 2.203-.018l5.922-3.42c.775-.447 1-1.463.553-2.238a1.697 1.697 0 0 0-.55-.568l-9.966-5.76 2.934-1.693 10.824 6.223.032 5.296-10.793 6.232-5.396-3.084c-.532-.283-1.135-.304-1.633-.017-.332.192-.845.71-.857 1.455l-.028 6.216-6.598 3.81Zm-2.918 3.604a1.661 1.661 0 1 0-2.878 1.66 1.661 1.661 0 0 0 2.878-1.66ZM4.043 15.43a1.662 1.662 0 0 0-3.323 0v8.45c0 .896.32 1.471 1.086 1.918l3.451 1.982-3.45 1.982C1.04 30.209.72 30.784.72 31.68v13.614c0 .895.32 1.47 1.086 1.918l5.624 3.26c.384.256.767.383 1.15.383.384 0 .767-.127 1.151-.383l11.822-6.84c.768-.447 1.087-1.022 1.087-1.917v-6.84c0-.703-.575-1.661-1.662-1.661a1.7 1.7 0 0 0-.766.192l-9.97 5.752V35.77l5.368-3.132c.511-.255.831-.767.831-1.405 0-.896-.703-1.599-1.598-1.599-.32 0-.575.064-.83.192l-5.88 3.388c-.767.447-1.086 1.023-1.086 1.918v6.839c0 .894.767 1.598 1.661 1.598.256 0 .512-.064.767-.192l9.97-5.753v3.388l-10.8 6.264-4.602-2.62V32.19l5.368-3.132c.511-.32.831-.83.831-1.406 0-.384-.192-1.087-.83-1.47l-5.37-3.132v-7.62Z"
          fill="url(#a)"
        />
        <defs>
          <linearGradient
            id="a"
            gradientUnits="userSpaceOnUse"
            x1=".131"
            x2="48.563"
            y1="27.564"
            y2="27.564"
          >
            <stop stop-color="#1047B4" />
            <stop offset="1" stop-color="#0CF" />
          </linearGradient>
        </defs>
      </svg>
    </div>
    <UiTransitionFade appear>
      <section v-if="!isLoading" class="w-xs p-4">
        <UiButton class="w-full" :loading="signInLoading" @click="onSignIn()">
          Sign in
        </UiButton>
      </section>
    </UiTransitionFade>
  </main>
  <div v-else-if="isAuthenticated" class="default-layout">
    <AppToolbar
      class="sticky top-0"
      :class="{ 'bg-transparent': underMaintenance }"
    >
      <div class="flex h-full items-center">
        <NuxtLink
          to="/"
          class="h1 flex items-center space-x-2 rounded pr-4 hover:underline hover:opacity-90"
        >
          <DisputesFindIcon class="h-10 w-11" />
          <span>Find a Claim</span>
        </NuxtLink>
      </div>
      <template #user-menu>
        <div
          v-if="underMaintenance"
          class="bg-warning-200 border-warning-dark text-warning-800 rounded border-2 border-dashed px-2 py-1 text-xs"
        >
          Under Maintenance
        </div>
        <AppUserMenu
          :logo-url="workspace?.logo ?? ''"
          :profile-img-url="user?.picture"
        >
          <ul class="flex flex-col p-2">
            <li v-if="user" class="p-1 px-2 pb-2">
              <h3
                v-if="user.name !== user.email"
                class="subtitle-2 text-neutral-900"
              >
                {{ user.name }}
              </h3>
              <p class="body-2 text-neutral-800">{{ user.email }}</p>
            </li>
            <li>
              <button class="user-menu-link" @click="onSignOut()">
                Log out
              </button>
            </li>
          </ul>
        </AppUserMenu>
      </template>
    </AppToolbar>

    <slot />

    <div
      v-if="underMaintenance && !isRialticUser"
      class="fixed inset-0 z-10 grid h-screen w-full items-center justify-center backdrop-blur-sm"
    >
      <div
        class="card border-warning bg-warning-100 border-4 border-dashed p-10 px-16 text-center"
      >
        <h2 class="h3 text-warning-700 pb-2">Under maintenance</h2>

        <p class="body-1 text-neutral-900">
          This service is currently under maintenance.
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped>
.user-menu-link {
  @apply inline-flex w-full rounded-lg px-4 py-2 text-left text-sm font-light hover:bg-neutral-100 hover:text-neutral-800;
}
.default-layout {
  @apply grid h-dvh bg-white;
  grid-template-rows: 64px auto;
}
</style>
